/**
 * SortableColumns Component (Local Enhanced Version)
 *
 * This is a local copy of the @praetorian-chariot/ui SortableColumns component
 * with enhancements for asset-specific requirements.
 *
 * Enhancements over library version:
 * - fixedLast columns support (always appear at end of table)
 * - Improved UI with "Fixed Last Columns" section
 * - React Portal rendering to escape overflow constraints (positioning fix)
 * - Dynamic positioning with viewport boundary detection
 * - Keyboard navigation (Escape key) and click outside detection
 *
 * Library Status: Being EOL'd
 * Migration Status: Asset table migrated, other tables pending
 *
 * @see README.md for usage guide and migration instructions
 */

import {
  Bars2Icon,
  ChevronDownIcon,
  Cog8ToothIcon,
} from '@heroicons/react/24/outline';
import { Button, ButtonProps, Checkbox } from '@praetorian-chariot/ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/utils/classname';

import { ColumnsConfigItem, SortableColumnsConfig } from './types';

// Icon sizes from Dropdown component
const iconSizes: Record<NonNullable<ButtonProps['size']>, string> = {
  xs: 'size-3',
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6',
  xl: 'size-7',
};

interface SortableColumnsTriggerProps {
  onClick?: () => void;
  className?: string;
  size?: ButtonProps['size'];
  isOpen?: boolean;
  placeholder?: string;
  styleType?: ButtonProps['styleType'];
  icon?: React.ReactNode;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
}

export const SortableColumnsTrigger: React.FC<SortableColumnsTriggerProps> = ({
  onClick,
  className,
  size = 'sm',
  isOpen = false,
  placeholder,
  styleType = 'tertiary',
  icon = <Cog8ToothIcon className={iconSizes.sm} />,
  'aria-expanded': ariaExpanded,
  'aria-controls': ariaControls,
}) => {
  return (
    <Button
      onClick={() => onClick?.()}
      label={placeholder}
      size={size}
      styleType={styleType}
      className={cn(
        isOpen &&
          'border-chariot-brand-primary bg-chariot-background-secondary',
        className
      )}
      endIcon={
        styleType !== 'icon' && (
          <ChevronDownIcon
            className={cn(
              'stroke-chariot-text-primary stroke-[1.5px]',
              iconSizes[size]
            )}
          />
        )
      }
      startIcon={icon}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      data-testid="sortable-columns-trigger"
    />
  );
};

interface LabelProps extends ColumnsConfigItem {
  size?: 'sm' | 'md';
  checkbox?: boolean;
  checked?: boolean;
  onToggleCheckbox?: () => void;
  disabled?: boolean;
  dataTestid?: string;
}

const Label: React.FC<LabelProps> = ({
  label,
  isDefault = false,
  size = 'md',
  checkbox = false,
  checked = false,
  onToggleCheckbox,
  disabled = false,
  dataTestid,
}: LabelProps) => {
  return (
    <div
      className={cn(
        'sortable-column-row flex items-center gap-2',
        checkbox && 'cursor-pointer'
      )}
      data-testid={dataTestid}
    >
      {checkbox && onToggleCheckbox && (
        <div className="shrink-0 items-center">
          <input
            type="checkbox"
            disabled={disabled}
            checked={checked || false}
            onChange={disabled ? undefined : onToggleCheckbox}
            className="hidden"
          />
          <button
            onClick={disabled ? undefined : onToggleCheckbox}
            disabled={disabled}
            className={cn(
              'sortable-column-checkbox-button flex items-center justify-center',
              checked && 'checked',
              !checked && 'unchecked'
            )}
          >
            <Checkbox isChecked={checked || false} disabled={disabled} />
          </button>
        </div>
      )}
      <p
        className={cn(
          'text-chariot-text-primary py-1',
          size === 'sm' && 'text-sm',
          size === 'md' && 'text-base'
        )}
        onClick={() => {
          if (checkbox && !disabled) {
            onToggleCheckbox?.();
          }
        }}
      >
        {label}
      </p>
      {isDefault && (
        <span className="text-chariot-text-secondary italic">(Default)</span>
      )}
      {checked && (
        <Bars2Icon
          data-testid="reorder-button"
          className="text-chariot-text-secondary ml-auto size-4"
        />
      )}
    </div>
  );
};

const Divider: React.FC = () => {
  return <div className="border-chariot-stroke my-1 h-px border-t" />;
};

export const SortableColumnsDropdown: React.FC<{
  defaultConfig: SortableColumnsConfig;
  activeColumns: string[] | undefined;
  isOpen: boolean;
  onChangeActiveColumns: (activeColumns: string[]) => void;
  onClose: () => void;
  className?: string;
  triggerRef: React.RefObject<HTMLDivElement | null>;
  position?: 'left' | 'right';
}> = ({
  defaultConfig,
  activeColumns,
  isOpen,
  onChangeActiveColumns,
  onClose,
  className,
  triggerRef,
  position = 'left',
}) => {
  const [config, setConfig] = useState<SortableColumnsConfig>(() => {
    const initialConfig: SortableColumnsConfig = {
      fixed: defaultConfig.fixed,
      fixedLast: defaultConfig.fixedLast,
      active: [],
      available: [],
    };

    // Use activeColumns if provided, otherwise use defaultConfig.active
    const activeColumnIds =
      activeColumns ?? defaultConfig.active.map(col => col.id);

    const allColumns = [...defaultConfig.active, ...defaultConfig.available];

    // Add active columns to initialConfig
    activeColumnIds.forEach(id => {
      const column = allColumns.find(col => col.id === id);
      if (column) {
        initialConfig.active.push(column);
      }
    });

    // Add available columns to initialConfig
    allColumns
      .filter(col => !activeColumnIds.includes(col.id))
      .forEach(column => {
        initialConfig.available.push(column);
      });

    return initialConfig;
  });
  const [dragItemIndex, setDragItemIndex] = useState<number>();
  const [dragOverItemIndex, setDragOverItemIndex] = useState<number>();
  const [dropdownCoords, setDropdownCoords] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !dropdownRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

    const offset = 8;
    let top = triggerRect.bottom + scrollTop + offset;
    let left =
      position === 'right'
        ? triggerRect.right + scrollLeft - dropdownRect.width
        : triggerRect.left + scrollLeft;

    // Ensure dropdown stays within viewport bounds
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < 0) left = 8;
    if (left + dropdownRect.width > viewportWidth) {
      left = viewportWidth - dropdownRect.width - 8;
    }

    // Check if dropdown would overflow bottom of viewport
    if (top + dropdownRect.height > viewportHeight + scrollTop) {
      // Position above trigger instead
      top = triggerRect.top + scrollTop - dropdownRect.height - offset;
    }

    if (top < scrollTop) top = scrollTop + 8;

    setDropdownCoords({ top, left });
  }, [triggerRef, position]);

  // Position calculation when dropdown opens or window resizes
  useEffect(() => {
    if (isOpen) {
      calculatePosition();
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition);
      return () => {
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', calculatePosition);
      };
    }
  }, [isOpen, calculatePosition]);

  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(calculatePosition, 0);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isOpen, calculatePosition]);

  // Handle click outside and escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent): void => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, triggerRef]);

  const handleToggleColumn = (
    id: ColumnsConfigItem['id'],
    columnType: 'active' | 'available' | 'fixed'
  ) => {
    // If in active, move to available
    if (columnType === 'active') {
      const column = config.active.find(col => col.id === id);
      if (!column) return;
      const newActive = config.active.filter(col => col.id !== id);
      setConfig({
        ...config,
        active: newActive,
        available: [...config.available, column],
      });
      onChangeActiveColumns(newActive.map(col => col.id));
      return;
    }

    // If in available, move to active
    if (columnType === 'available') {
      const column = config.available.find(col => col.id === id);
      if (!column) return;
      const newActive = [...config.active, column];
      setConfig({
        ...config,
        active: newActive,
        available: config.available.filter(col => col.id !== id),
      });
      onChangeActiveColumns(newActive.map(col => col.id));
      return;
    }
  };

  function handleDragStart(
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) {
    event.stopPropagation();
    setDragItemIndex(index);
  }

  function handleDragOver(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleDrop(index: number) {
    if (dragItemIndex !== undefined && dragItemIndex !== index) {
      const updatedColumns = [...config.active];
      const draggedItem = updatedColumns[dragItemIndex];
      updatedColumns.splice(dragItemIndex, 1);
      updatedColumns.splice(index, 0, draggedItem);
      setConfig({ ...config, active: updatedColumns });
      onChangeActiveColumns(updatedColumns.map(col => col.id));
    }
    setDragItemIndex(undefined);
    setDragOverItemIndex(undefined);
  }

  function handleDragEnter(index: number) {
    setDragOverItemIndex(index);
  }

  function handleDragEnd(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault();
    event.stopPropagation();
    setDragItemIndex(undefined);
    setDragOverItemIndex(undefined);
  }

  if (!isOpen) {
    return null;
  }

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={cn('fixed z-[1000] w-[400px] overflow-y-auto', className)}
      style={{
        top: dropdownCoords.top,
        left: dropdownCoords.left,
      }}
      data-testid="sortable-columns-dropdown"
      role="dialog"
      aria-label="Column selector"
    >
      <div className="flex flex-col gap-1 p-4">
        {config.fixed.length > 0 && (
          <>
            <Label
              label="Fixed Columns"
              id="fixed-columns"
              size="sm"
              dataTestid="fixed-column-label"
            />
            {config.fixed.map(col => (
              <Label {...col} dataTestid="fixed-column-value" key={col.id} />
            ))}
            {(config.active.length > 0 || config.available.length > 0) && (
              <Divider />
            )}
          </>
        )}
        {config.active.length > 0 && (
          <>
            <Label
              label="Active Columns"
              id="active-columns"
              dataTestid="active-column-label"
              size="sm"
            />
            {config.active.map((col, index) => (
              <div
                key={col.id}
                draggable
                onDragStart={event => {
                  handleDragStart(event, index);
                }}
                onDragOver={event => {
                  handleDragOver(event);
                }}
                onDrop={() => {
                  handleDrop(index);
                }}
                onDragEnter={() => {
                  handleDragEnter(index);
                }}
                onDragEnd={handleDragEnd}
                className={cn(
                  'relative translate-z-0',
                  dragOverItemIndex === index && 'bg-chariot-transparent',
                  dragItemIndex === index && 'bg-chariot-background-primary'
                )}
              >
                <Label
                  {...col}
                  key={col.id}
                  checkbox={true}
                  disabled={col.disabled}
                  checked={true}
                  onToggleCheckbox={() => {
                    handleToggleColumn(col.id, 'active');
                  }}
                  dataTestid="active-column-value"
                />
              </div>
            ))}
            {config.available.length > 0 && <Divider />}
          </>
        )}
        {config.available.length > 0 && (
          <>
            <Label
              label="Available Columns"
              id="available-columns"
              dataTestid="available-column-label"
              size="sm"
            />
            {config.available.map(col => (
              <Label
                {...col}
                key={col.id}
                checkbox={true}
                checked={false}
                onToggleCheckbox={() => {
                  handleToggleColumn(col.id, 'available');
                }}
                dataTestid="available-column-value"
              />
            ))}
          </>
        )}
        {/* ENHANCEMENT: Fixed Last Columns Section */}
        {defaultConfig.fixedLast && defaultConfig.fixedLast.length > 0 && (
          <>
            <Divider />
            <Label
              label="Fixed Last Columns"
              id="fixed-last-columns"
              size="sm"
              dataTestid="fixed-last-column-label"
            />
            <p className="text-chariot-text-secondary px-1 py-1 text-xs italic">
              These columns always appear at the end
            </p>
            {defaultConfig.fixedLast.map(col => (
              <Label
                {...col}
                dataTestid="fixed-last-column-value"
                key={col.id}
              />
            ))}
          </>
        )}
      </div>
      <div className="bg-chariot-background-primary sticky bottom-0 px-4 pt-2 pb-4">
        <Button
          onClick={() => {
            setConfig(defaultConfig);
            onChangeActiveColumns(defaultConfig.active.map(col => col.id));
          }}
          label="Reset Defaults"
          styleType="primary"
          className="w-full"
        />
      </div>
    </div>
  );

  return createPortal(dropdownContent, document.body);
};

interface SortableColumnsProps {
  defaultConfig: SortableColumnsConfig;
  activeColumns: string[] | undefined;
  position?: 'left' | 'right';
  onChangeActiveColumns: (activeColumns: string[]) => void;
  className?: string;
  icon?: React.ReactNode;
  listClassName?: string;
  placeholder?: string;
  size?: ButtonProps['size'];
  styleType?: ButtonProps['styleType'];
  triggerClassName?: string;
}

export const SortableColumns: React.FC<SortableColumnsProps> = ({
  defaultConfig,
  position = 'left',
  activeColumns,
  onChangeActiveColumns,
  className,
  icon = <Cog8ToothIcon className="size-4" />,
  listClassName,
  placeholder = 'Edit Columns',
  size,
  styleType = 'tertiary',
  triggerClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleTriggerClick = (): void => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={cn('relative w-fit', className)} ref={triggerRef}>
      <SortableColumnsTrigger
        onClick={handleTriggerClick}
        size={size}
        isOpen={isOpen}
        className={triggerClassName}
        placeholder={placeholder}
        styleType={styleType}
        icon={icon}
        aria-expanded={isOpen}
        aria-controls="sortable-columns-dropdown"
      />

      <SortableColumnsDropdown
        isOpen={isOpen}
        defaultConfig={defaultConfig}
        activeColumns={activeColumns}
        onChangeActiveColumns={onChangeActiveColumns}
        onClose={() => {
          setIsOpen(false);
        }}
        className={cn(
          'bg-chariot-background-primary border-chariot-brand-primary rounded border',
          listClassName
        )}
        triggerRef={triggerRef}
        position={position}
      />
    </div>
  );
};
